import { useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  F0FormSubmitResult,
  F0WizardForm,
  F0WizardFormPerSectionSubmitArg,
} from '@factorialco/f0-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { i18nDateToString } from 'modules/core/lib/dates'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/Tracking'

import { useCreateBudget } from 'modules/finance/hooks/useBudgets'
import { events } from 'modules/finance/lib/events'

import type { BudgetExceededAmounts } from './types'
import { ExpensesBudgetSchema, useExpensesBudgetForm } from './form'

interface Props {
  legalEntities: Array<{ id: string; currency: string; main: boolean }>
  onSuccess: (budgetId: string) => void
  onError: () => void
}

export const NewBudgetWizard = ({ legalEntities, onSuccess, onError }: Props) => {
  const session = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { createBudget } = useCreateBudget()

  const currencyMap = useMemo(
    () => Object.fromEntries(legalEntities.map((le) => [le.id, le.currency])),
    [legalEntities]
  )

  const mainLegalEntity = useMemo(
    () => legalEntities.find((le) => le.main) ?? legalEntities[0],
    [legalEntities]
  )

  const budgetExceededRef = useRef<BudgetExceededAmounts | undefined>(undefined)
  const budgetAmountOverrideRef = useRef<number | undefined>(undefined)

  const handleSubmit = useCallback(
    async (
      arg: F0WizardFormPerSectionSubmitArg<ExpensesBudgetSchema>
    ): Promise<F0FormSubmitResult> => {
      if (arg.sectionId !== 'spenders') {
        return { success: true }
      }

      const { legalEntityId, name, description } = arg.fullData.basicDetails
      const { ownerEmployeeIds, viewerEmployeeIds } = arg.fullData.budgetAccess
      const { effectiveFrom, effectiveUntil, periodicity, amountCents } = arg.fullData.budgetLimits
      const { spendersField } = arg.fullData.spenders
      const { allocationType, allocatedAmountCents, spenderSelection } = spendersField

      if (!legalEntityId || !periodicity) {
        return { success: false, errors: {} }
      }

      // Validate allocated amount for equal allocation
      if (allocationType === 'equal') {
        if (allocatedAmountCents == null) {
          return {
            success: false,
            errors: {
              'spenders.spendersField': i18n.t('budgets.forms.errors.mandatory'),
            },
          }
        }
        if (allocatedAmountCents < 0) {
          return {
            success: false,
            errors: {
              'spenders.spendersField': i18n.t('budgets.forms.errors.invalid_amount'),
            },
          }
        }
        // Validate budget not exceeded for manual selections
        const spenderCount = spenderSelection.type === 'manual' ? spenderSelection.ids.length : 0
        const totalAllocatedAmount = allocatedAmountCents * spenderCount
        if (spenderCount && totalAllocatedAmount > amountCents) {
          budgetExceededRef.current = {
            budgetAmount: amountCents,
            allocatedAmount: allocatedAmountCents,
            totalAllocatedAmount,
            maxAllocationAmount: Math.floor((amountCents / spenderCount) * 100) / 100,
          }
          return {
            success: false,
            errors: {
              'spenders.spendersField': i18n.t('budgets.forms.errors.allocation_exceeded'),
            },
          }
        }
      }

      const ownerIds = ownerEmployeeIds.type === 'manual' ? ownerEmployeeIds.ids : []
      const viewerIds = viewerEmployeeIds.type === 'manual' ? viewerEmployeeIds.ids : []

      const effectiveFromStr = i18nDateToString(effectiveFrom, 'backend_datetime')
      const effectiveUntilStr = effectiveUntil
        ? i18nDateToString(effectiveUntil, 'backend_datetime')
        : undefined

      const finalAmountCents = budgetAmountOverrideRef.current ?? amountCents
      const response = await createBudget({
        name,
        description,
        legalEntityId,
        amountCents: toCents(finalAmountCents),
        effectiveFrom: effectiveFromStr,
        effectiveUntil: effectiveUntilStr,
        periodicity,
        ownerEmployeeIds: ownerIds,
        viewerEmployeeIds: viewerIds,
        spenderEmployeeIds: [],
        spenderEmployeeSelection: spenderSelection,
        ...(allocationType === 'equal' &&
          allocatedAmountCents != null && {
            allocatedAmountCents: toCents(allocatedAmountCents),
          }),
        budgetableBy: 'expensable',
      })

      let formErrors: Record<string, string> = {}
      const result = handleResult(response, (errors) => {
        formErrors = errors
      })

      if (!result.success) {
        return { success: false, errors: formErrors }
      }

      track(events.completeAddBudget, { budgetId: result.data.budget?.id })

      if (result.data.budget?.id) {
        onSuccess(result.data.budget.id)
      } else {
        onError()
      }

      return { success: true }
    },
    [createBudget, onSuccess, onError]
  )

  const { formDefinition, steps } = useExpensesBudgetForm({
    defaultOwnerEmployeeId: session.employee.id,
    defaultLegalEntityId: mainLegalEntity?.id,
    currency: mainLegalEntity?.currency ?? session.company.currency ?? '',
    currencyMap,
    onSubmit: handleSubmit,
    budgetExceededRef,
    budgetAmountOverrideRef,
  })

  return (
    <F0WizardForm
      isOpen
      title={i18n.t('budgets.forms.new_budget.title')}
      formDefinition={formDefinition}
      steps={steps}
      onClose={() => navigate(navigation.expenses.budgets.fullPath())}
    />
  )
}
